/**
 * Column Factories
 *
 * Factory functions for creating common column types with consistent
 * edit cell rendering and behavior.
 */

import React from 'react'
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, GridValueFormatter } from '@mui/x-data-grid-premium'
import { Avatar, AvatarGroup, Box, Chip, Tooltip, Typography } from '@mui/material'
import { EditTextCell, EditSelectCell, EditAutocompleteCell } from 'components/DataGrid/EditCells'
import { GridOptions, SelectOption } from '../types/EntityConfig'

/**
 * Column width presets
 */
export const COLUMN_WIDTHS = {
  checkbox: 50,
  avatar: 55,
  text: 150,
  textWide: 250,
  select: 120,
  date: 110,
  number: 100,
  actions: 80,
}

/**
 * Create a text column with optional inline editing
 */
export const createTextColumn = (
  field: string,
  headerName: string,
  options: Partial<GridColDef> & { editable?: boolean; placeholder?: string; inputType?: 'text' | 'email' | 'url' } = {}
): GridColDef => {
  const { placeholder, inputType, ...gridOptions } = options
  return {
    field,
    headerName,
    width: options.width || COLUMN_WIDTHS.text,
    editable: options.editable ?? false,
    ...gridOptions,
    ...(options.editable && {
      renderEditCell: (params: GridRenderEditCellParams) =>
        React.createElement(EditTextCell, {
          params,
          placeholder,
          type: inputType,
        }),
    }),
  }
}

/**
 * Create a select column with dropdown editing
 */
export const createSelectColumn = (
  field: string,
  headerName: string,
  selectOptions: SelectOption[],
  options: Partial<GridColDef> & { editable?: boolean; getOptionColor?: (opt: SelectOption) => string } = {}
): GridColDef => ({
  field,
  headerName,
  width: options.width || COLUMN_WIDTHS.select,
  editable: options.editable ?? true,
  ...options,
  renderCell: (params: GridRenderCellParams) => {
    const option = selectOptions.find(opt => opt.value === params.value)
    if (!option) return params.value || ''

    const color = options.getOptionColor?.(option)
    if (color) {
      return React.createElement(
        Chip,
        {
          label: option.label,
          size: 'small',
          sx: { backgroundColor: color, color: 'white' },
        }
      )
    }
    return option.label
  },
  renderEditCell: (params: GridRenderEditCellParams) =>
    React.createElement(EditSelectCell, {
      params,
      options: selectOptions,
      getOptionLabel: (opt: SelectOption) => opt.label,
      getOptionValue: (opt: SelectOption) => opt.value,
      getOptionColor: options.getOptionColor,
    }),
})

/**
 * Create a team member column (user autocomplete with avatars)
 */
export const createTeamMemberColumn = (
  field: string,
  headerName: string,
  gridOptions: GridOptions,
  options: Partial<GridColDef> = {}
): GridColDef => {
  const roleKey = field.replace('team.', '')

  return {
    field,
    headerName,
    width: options.width || COLUMN_WIDTHS.avatar,
    editable: true,
    ...options,
    valueGetter: (_, row) => {
      // Try _resolvedTeam first (pre-resolved from server)
      const resolved = row?._resolvedTeam?.[roleKey]
      if (resolved && Array.isArray(resolved)) return resolved

      // Otherwise try to resolve from team object
      const uuids = row?.team?.[roleKey]
      if (!uuids || !Array.isArray(uuids)) return []

      // Map UUIDs to user objects
      return uuids
        .map((uuid: string) => gridOptions.users?.find(u => u.user_uuid === uuid))
        .filter(Boolean)
    },
    valueSetter: (value, row) => {
      // The edit cell returns user objects, we store them directly
      // The processRowUpdate will extract UUIDs
      return { ...row, [field]: value }
    },
    renderCell: (params: GridRenderCellParams) => {
      const users = params.value
      if (!users || !Array.isArray(users) || users.length === 0) return null

      return React.createElement(
        AvatarGroup,
        { max: 3, sx: { '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 } } },
        users.map((user: any) =>
          React.createElement(
            Tooltip,
            {
              key: user.user_uuid || user.name,
              title: user.name || '',
              children: React.createElement(Avatar, {
                src: user.image_512 || user.image,
                alt: user.name,
                sx: { width: 28, height: 28 },
              }),
            }
          )
        )
      )
    },
    renderEditCell: (params: GridRenderEditCellParams) =>
      React.createElement(EditAutocompleteCell, {
        params: {
          ...params,
          value: params.value || [],
          field,
        },
        options: gridOptions.users || [],
        getOptionLabel: (u: any) => u.name || '',
        getOptionValue: (u: any) => u.user_uuid,
        getOptionImage: (u: any) => u.image_512 || u.image,
        multiple: true,
        placeholder: 'Search users...',
      }),
  }
}

/**
 * Create an organization reference column
 */
export const createOrgReferenceColumn = (
  field: string,
  headerName: string,
  gridOptions: GridOptions,
  options: Partial<GridColDef> & { multiple?: boolean } = {}
): GridColDef => ({
  field,
  headerName,
  width: options.width || COLUMN_WIDTHS.text,
  editable: true,
  ...options,
  valueGetter: (_, row) => {
    const value = row?.[field]
    if (!value) return options.multiple ? [] : null

    // If already resolved objects
    if (Array.isArray(value) && value[0]?.name) return value
    if (value?.name) return [value]

    // Resolve from UUIDs
    if (Array.isArray(value)) {
      return value
        .map((uuid: string) => gridOptions.organizations?.find(o => o.org_uuid === uuid))
        .filter(Boolean)
    }

    const org = gridOptions.organizations?.find(o => o.org_uuid === value)
    return org ? [org] : []
  },
  valueSetter: (value, row) => ({ ...row, [field]: value }),
  renderCell: (params: GridRenderCellParams) => {
    const orgs = params.value
    if (!orgs || (Array.isArray(orgs) && orgs.length === 0)) return ''
    if (Array.isArray(orgs)) {
      return orgs.map((o: any) => o.name).join(', ')
    }
    return orgs?.name || ''
  },
  renderEditCell: (params: GridRenderEditCellParams) =>
    React.createElement(EditAutocompleteCell, {
      params: {
        ...params,
        value: params.value || [],
      },
      options: gridOptions.organizations || [],
      getOptionLabel: (org: any) => org.name || '',
      getOptionValue: (org: any) => org.org_uuid,
      multiple: options.multiple ?? true,
      placeholder: 'Search organizations...',
    }),
})

/**
 * Create a contact column
 */
export const createContactColumn = (
  field: string,
  headerName: string,
  gridOptions: GridOptions,
  options: Partial<GridColDef> & { multiple?: boolean } = {}
): GridColDef => ({
  field,
  headerName,
  width: options.width || COLUMN_WIDTHS.text,
  editable: true,
  ...options,
  valueGetter: (_, row) => {
    const value = row?.[field]
    if (!value) return options.multiple ? [] : null

    // If already resolved objects
    if (Array.isArray(value) && value[0]?.name) return value
    if (value?.name) return options.multiple ? [value] : value

    // Resolve from UUIDs
    if (Array.isArray(value)) {
      return value
        .map((uuid: string) => gridOptions.contacts?.find(c => c.contacts_uuid === uuid))
        .filter(Boolean)
    }

    const contact = gridOptions.contacts?.find(c => c.contacts_uuid === value)
    return options.multiple ? (contact ? [contact] : []) : contact
  },
  valueSetter: (value, row) => ({ ...row, [field]: value }),
  renderCell: (params: GridRenderCellParams) => {
    const contacts = params.value
    if (!contacts) return ''
    if (Array.isArray(contacts)) {
      return contacts.map((c: any) => c.name).join(', ')
    }
    return contacts?.name || ''
  },
  renderEditCell: (params: GridRenderEditCellParams) =>
    React.createElement(EditAutocompleteCell, {
      params: {
        ...params,
        value: options.multiple
          ? params.value || []
          : params.value
            ? [params.value]
            : [],
      },
      options: gridOptions.contacts || [],
      getOptionLabel: (c: any) => c.name || '',
      getOptionValue: (c: any) => c.contacts_uuid,
      multiple: options.multiple ?? false,
      placeholder: 'Search contacts...',
    }),
})

/**
 * Create a single user reference column (e.g., owner)
 */
export const createUserColumn = (
  field: string,
  headerName: string,
  gridOptions: GridOptions,
  options: Partial<GridColDef> = {}
): GridColDef => ({
  field,
  headerName,
  width: options.width || COLUMN_WIDTHS.text,
  editable: true,
  ...options,
  valueGetter: (_, row) => {
    const value = row?.[field]
    if (!value) return null

    // If already resolved object
    if (value?.name) return value

    // Resolve from UUID (might be array with single item)
    const uuid = Array.isArray(value) ? value[0] : value
    return gridOptions.users?.find(u => u.user_uuid === uuid) || null
  },
  valueSetter: (value, row) => ({ ...row, [field]: value }),
  renderCell: (params: GridRenderCellParams) => {
    const user = params.value
    if (!user) return ''

    return React.createElement(
      Box,
      { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
      React.createElement(Avatar, {
        src: user.image_512 || user.image,
        alt: user.name,
        sx: { width: 24, height: 24 },
      }),
      React.createElement(Typography, { variant: 'body2' }, user.name)
    )
  },
  renderEditCell: (params: GridRenderEditCellParams) =>
    React.createElement(EditAutocompleteCell, {
      params: {
        ...params,
        value: params.value ? [params.value] : [],
      },
      options: gridOptions.users || [],
      getOptionLabel: (u: any) => u.name || '',
      getOptionValue: (u: any) => u.user_uuid,
      getOptionImage: (u: any) => u.image_512 || u.image,
      multiple: false,
      placeholder: 'Search users...',
    }),
})

/**
 * Currency formatter
 */
export const currencyFormatter: GridValueFormatter = ({ value }) =>
  value == null
    ? ''
    : (value as number).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })

/**
 * Create a currency column
 */
export const createCurrencyColumn = (
  field: string,
  headerName: string,
  options: Partial<GridColDef> = {}
): GridColDef => ({
  field,
  headerName,
  width: options.width || COLUMN_WIDTHS.number,
  type: 'number',
  valueFormatter: currencyFormatter,
  ...options,
})

/**
 * Create a date column
 */
export const createDateColumn = (
  field: string,
  headerName: string,
  options: Partial<GridColDef> = {}
): GridColDef => ({
  field,
  headerName,
  width: options.width || COLUMN_WIDTHS.date,
  type: 'date',
  valueGetter: (_, row) => {
    const value = row?.[field]
    return value ? new Date(value) : null
  },
  ...options,
})
